'use client';

import { useEffect, useState } from 'react';
import { formatDateString, formatDateTime } from '@bridge/utils';

interface ClientDateTimeProps {
  /**
   * The date string to format
   */
  date: string;
  /**
   * Intl.DateTimeFormat options for formatting the date
   */
  options?: Intl.DateTimeFormatOptions;
  /**
   * Optional fallback to show during SSR/hydration
   * @default '...'
   */
  fallback?: string;
  /**
   * Optional className to apply to the wrapper span
   */
  className?: string;
  /**
   * When true, uses formatDateTime which outputs "M/D/YY at H:MMAM/PM UTC"
   * @default false
   */
  showDateTime?: boolean;
}

/**
 * ClientDateTime component that prevents hydration errors when formatting dates with timezone information.
 *
 * This component defers date formatting until after the component mounts on the client,
 * preventing server/client mismatches caused by timezone differences.
 *
 * @example
 * ```tsx
 * <ClientDateTime
 *   date="2025-01-15T15:30:00Z"
 *   options={{
 *     month: '2-digit',
 *     day: '2-digit',
 *     year: '2-digit',
 *     hour: 'numeric',
 *     minute: '2-digit',
 *     timeZoneName: 'short'
 *   }}
 * />
 * ```
 */
export default function ClientDateTime({
  date,
  options,
  fallback = '...',
  className,
  showDateTime = false,
}: ClientDateTimeProps) {
  const [formattedDate, setFormattedDate] = useState<string | null>(null);

  useEffect(() => {
    if (showDateTime) {
      setFormattedDate(formatDateTime(date));
    } else {
      setFormattedDate(formatDateString(date, options));
    }
  }, [date, options, showDateTime]);

  return <span className={className}>{formattedDate ?? fallback}</span>;
}
